import { http, type ApiResponse } from '@/shared/api'
import { normalizePointBalance } from '@/shared/model/point-balance'
import type { EnterTeamResult } from '../model/types'

function asRecord(value: unknown): Record<string, unknown> | null {
  if (!value || typeof value !== 'object' || Array.isArray(value)) {
    return null
  }

  return value as Record<string, unknown>
}

function readString(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return ''
  }

  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'string' && value.trim()) {
      return value.trim()
    }

    if (typeof value === 'number' && Number.isFinite(value)) {
      return String(value)
    }
  }

  return ''
}

function readNumber(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return null
  }

  for (const key of keys) {
    const value = record[key]

    if (typeof value === 'number' && Number.isFinite(value)) {
      return value
    }

    if (typeof value === 'string') {
      const parsed = Number(value)
      if (Number.isFinite(parsed)) {
        return parsed
      }
    }
  }

  return null
}

function hasOwnKey(record: Record<string, unknown> | null, keys: string[]) {
  if (!record) {
    return false
  }

  return keys.some((key) => Object.prototype.hasOwnProperty.call(record, key))
}

function buildTeamSessionResult(
  record: Record<string, unknown> | null,
  fallbackTeamId = '',
): EnterTeamResult | null {
  if (!record) {
    return null
  }

  const isPersonalTeam = readNumber(record, ['is_personal_team'])
  const collaborationEnabled = readNumber(record, ['collaboration_enabled'])
  const hasValidSeat = readNumber(record, ['has_valid_seat'])
  const hasTeamAvatarField = hasOwnKey(record, ['team_avatar', 'avatar', 'cover'])

  return {
    teamId: readString(record, ['team_id', 'id']) || fallbackTeamId,
    teamName: readString(record, ['team_name', 'name']),
    teamAvatar: hasTeamAvatarField ? readString(record, ['team_avatar', 'avatar', 'cover']) : undefined,
    hasTeamAvatarField,
    teamKind: readString(record, ['team_kind', 'kind']) || undefined,
    currentPlanType: readString(record, ['current_plan_type', 'plan_type']) || undefined,
    planName: readString(record, ['plan_name', 'current_plan_name']) || undefined,
    planStatus: readString(record, ['plan_status', 'status_text']) || undefined,
    concurrencyLimit: readNumber(record, ['concurrency_limit']) ?? undefined,
    isPersonalTeam: isPersonalTeam === null ? undefined : isPersonalTeam === 1,
    collaborationEnabled: collaborationEnabled === null ? undefined : collaborationEnabled === 1,
    memberRole: readString(record, ['member_role', 'role']),
    graceExpireTime: readNumber(record, ['grace_expire_time']) ?? undefined,
    seatStatus: readString(record, ['seat_status']) || undefined,
    hasValidSeat: hasValidSeat === null ? undefined : hasValidSeat === 1,
    enterMode: readString(record, ['enter_mode']) || undefined,
    pointLimitMonth: readNumber(record, ['point_limit_month', 'month_point_limit', 'month_quota_total', 'quota_total']) ?? undefined,
    pointUsedMonth: readNumber(record, ['point_used_month', 'month_point_used', 'month_quota_used', 'quota_used']) ?? undefined,
    pointRemainMonth: readNumber(record, ['point_remain_month', 'month_point_remain', 'point_remain', 'point']) ?? undefined,
    teamPointLimitMonth: readNumber(record, ['team_point_limit_month', 'team_point_total', 'point_total']) ?? undefined,
    pointBalancePermanent: readNumber(record, ['point_balance_permanent', 'team_point_total', 'point_total']) ?? undefined,
    pointBalance: normalizePointBalance(record?.point_balance),
  }
}

export async function requestTeamList(): Promise<ApiResponse<unknown>> {
  return http.post<unknown>('/zs/AiCanvasTeam/teamList')
}

export async function requestEnterTeam(teamId: string): Promise<ApiResponse<EnterTeamResult>> {
  const response = await http.post<unknown>('/zs/AiCanvasTeam/enterTeam', {
    team_id: teamId,
  })

  if (response.code !== 1 || !response.data) {
    return {
      code: response.code,
      msg: response.msg || '切换团队失败',
    }
  }

  const data = buildTeamSessionResult(asRecord(response.data), teamId)

  if (!data) {
    return {
      code: -1,
      msg: response.msg || '切换团队失败，响应数据无效',
    }
  }

  return {
    code: 1,
    msg: response.msg || '切换成功',
    data,
  }
}

export async function requestCurrentTeam(): Promise<ApiResponse<EnterTeamResult>> {
  const response = await http.post<unknown>('/zs/AiCanvasTeam/getCurrentTeam')

  if (response.code !== 1 || !response.data) {
    return {
      code: response.code,
      msg: response.msg || '获取当前团队失败',
    }
  }

  const data = buildTeamSessionResult(asRecord(response.data))

  if (!data) {
    return {
      code: -1,
      msg: response.msg || '获取当前团队失败，响应数据无效',
    }
  }

  return {
    code: 1,
    msg: response.msg || '获取成功',
    data,
  }
}
